import { useEffect, useState } from "react";
import { Coins } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, fmtDate } from "@/lib/api";
import { fmtClock, ms } from "@/lib/goldrain";
import { Input } from "@/components/ui/input";

function Active({ ev, onCancel }) {
  const [t, setT] = useState(Date.now());
  useEffect(() => { const iv = setInterval(() => setT(Date.now()), 1000); return () => clearInterval(iv); }, []);
  const raining = t >= ms(ev.start_at);
  const left = Math.max(0, Math.ceil(((raining ? ms(ev.end_at) : ms(ev.start_at)) - t) / 1000));
  return (
    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="admin-goldrain-active">
      <p className="font-bold text-amber-900">{raining ? "Goldregen läuft – noch " : "Goldregen startet in "}<span className="font-mono" data-testid="admin-goldrain-left">{fmtClock(left)}</span></p>
      <button onClick={onCancel} data-testid="admin-goldrain-cancel" className="px-4 h-9 rounded-full bg-white border border-red-200 text-red-600 text-xs font-bold">Abbrechen</button>
    </div>
  );
}

const FIELDS = [["countdown_min", "Countdown (Minuten)"], ["duration_sec", "Dauer Münzregen (Sekunden)"], ["coins", "Anzahl Münzen"]];

function Settings({ settings, limits, onSaved }) {
  const [f, setF] = useState(settings);
  const save = async () => {
    try { await api.put("/admin/goldrain/settings", f); toast.success("Goldregen-Einstellungen gespeichert"); onSaved(); } catch (e) { toast.error(formatErr(e)); }
  };
  return (
    <div className="rounded-2xl border border-pink-100 p-4 space-y-3" data-testid="admin-goldrain-settings">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Einstellungen für den nächsten Goldregen</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {FIELDS.map(([k, l]) => (
          <label key={k} className="space-y-1.5 block">
            <span className="text-xs font-medium">{l} <span className="text-slate-400">({limits[k][0]}–{limits[k][1]})</span></span>
            <Input type="number" min={limits[k][0]} max={limits[k][1]} data-testid={`admin-goldrain-${k}`} className="rounded-xl" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
          </label>
        ))}
      </div>
      <button onClick={save} data-testid="admin-goldrain-save" className="px-5 h-10 rounded-full pink-gradient text-white text-sm font-bold">Speichern</button>
    </div>
  );
}

export function GoldRainAdmin() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/goldrain").then(({ data }) => setD(data)).catch(() => {});
  useEffect(() => { load(); const iv = setInterval(load, 10000); return () => clearInterval(iv); }, []);
  const call = async (path, msg, ask) => {
    if (ask && !window.confirm(ask)) return;
    setBusy(true);
    try { await api.post(path); toast.success(msg); window.dispatchEvent(new Event("goldrain-changed")); await load(); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <div className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-5" data-testid="admin-goldrain">
      <div>
        <h3 className="font-bold text-lg flex items-center gap-2"><Coins className="w-5 h-5 text-amber-500" /> Goldregen</h3>
        <p className="text-sm text-slate-500 mt-1" data-testid="admin-goldrain-desc">Ein Klick: 30 Sekunden Alarm + Push „ACHTUNG! GOLD REGEN STARTET IN {d.settings.countdown_min} {d.settings.countdown_min === 1 ? "MINUTE" : "MINUTEN"}!“ an alle, Countdown oben auf jeder Seite, danach {d.settings.duration_sec} Sekunden lang {d.settings.coins} fallende Goldmünzen (1 Münze = 1 Punkt). Am Ende die Top 3 – und die Sieger-Karte erscheint automatisch im Feed.</p>
      </div>
      {d.active ? <Active ev={d.active} onCancel={() => call("/admin/goldrain/cancel", "Goldregen abgebrochen", "Goldregen wirklich abbrechen?")} />
        : <button disabled={busy} onClick={() => call("/admin/goldrain/start", "Goldregen gestartet – Push ist raus!", "Goldregen jetzt starten? Alle Mitglieder bekommen sofort Alarm & Push.")}
            data-testid="admin-goldrain-start" className="w-full sm:w-auto px-8 h-14 rounded-full gold-banner text-amber-950 font-extrabold text-lg shadow-lg shadow-amber-300/50 hover:scale-[1.02] transition-transform disabled:opacity-60">
            Goldregen starten
          </button>}
      <Settings key={JSON.stringify(d.settings)} settings={d.settings} limits={d.limits} onSaved={load} />
      {d.events.filter((e) => e.state === "done").length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Letzte Goldregen</p>
          {d.events.filter((e) => e.state === "done").map((e) => (
            <div key={e.id} className="rounded-2xl border border-pink-100 p-3 text-sm" data-testid={`admin-goldrain-event-${e.id}`}>
              <p className="font-semibold">{fmtDate(e.start_at)} · {e.players} Mitspieler · {e.caught} Münzen gefangen</p>
              <p className="text-slate-500 text-xs mt-1">{e.top.length ? e.top.map((p, i) => `${i + 1}. ${p.name} (${p.coins})`).join(" · ") : "Niemand hat Münzen gefangen"}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
